import { useEffect, type RefObject } from 'react'
import { pushEscape } from './escape'
import { inertOthers, lockScroll } from './document'
import { trapFocus } from './focus'

export interface ModalLayerOptions {
  open: boolean
  /** Called on Escape. Omit to make Escape do nothing (e.g. a prompt without cancel). */
  onEscape?: (() => void) | undefined
  /** Element that receives focus first. */
  initialFocus?: RefObject<HTMLElement | null> | undefined
}

/**
 * Everything a modal surface needs while open: Escape stack, focus trap with
 * restore, inert background and scroll lock. `ref` is the element that
 * contains the whole layer (scrim included) inside the portal.
 */
export function useModalLayer(ref: RefObject<HTMLElement | null>, options: ModalLayerOptions) {
  const { open, onEscape, initialFocus } = options

  useEffect(() => {
    const el = ref.current
    if (!open || !el) return
    const host = el.closest('[data-portal]') ?? el
    const releaseInert = inertOthers(host)
    const releaseScroll = lockScroll()
    const releaseTrap = trapFocus(el, { initial: initialFocus?.current ?? null })
    return () => {
      // Order matters: the opener is inside the inert background, and an
      // inert element cannot take focus. Lift inert first, restore focus last.
      releaseInert()
      releaseScroll()
      releaseTrap()
    }
    // initialFocus is read once on open, on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ref])

  useEffect(() => {
    if (!open) return
    return pushEscape(() => {
      if (!onEscape) return false
      onEscape()
      return true
    })
  }, [open, onEscape])
}
